
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        // Element cache
        this.elements = {
          liveRegion:  this.querySelector('[id^="GalleryStatus"]'),
          viewer:      this.querySelector('[id^="GalleryViewer"]'),
          thumbnails:  this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.zoomState = 0; // 0 = none, 1 = zoom-1, 2 = zoom-2

        /* ========= MAIN ARROWS (INFINITE WRAP + NEVER LOCK) ========= */
        this.prevButton = this.querySelector('.main-gallery-prev');
        this.nextButton = this.querySelector('.main-gallery-next');

        if (this.prevButton) {
          this.prevButton.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
            this.goPrevWrapped();
          });
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
            this.goNextWrapped();
          });
        }

        // Keep main arrows enabled (wrap mode never disables)
        this.enableMainArrows();
        this.installMainArrowsObserver();

        /* ========= VIEWER CLICK-TO-ZOOM (only when no thumbs) ========= */
        if (!this.elements.thumbnails) {
          this.elements.viewer.addEventListener('click', this.handleMainViewerClick.bind(this));
        }

        /* ========= VIEWER slideChanged → reset zoom + sync thumbs ========= */
        this.elements.viewer.addEventListener('slideChanged', this.debounce(this.onSlideChanged.bind(this), 250));

        /* ========= THUMBNAIL: click → set active media ========= */
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((node) => {
            const btn = node.querySelector('button');
            if (!btn) return;
            btn.addEventListener('click', (e) => {
              e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
              this.setActiveMedia(node.dataset.target, false);
            });
          });
        }

        /* ========= THUMBNAIL ARROWS (INFINITE WRAP) ========= */
        if (this.elements.thumbnails) {
          this.thumbButtons = {
            prev: this.elements.thumbnails.querySelector('.slider-button--prev'),
            next: this.elements.thumbnails.querySelector('.slider-button--next'),
          };

          if (this.thumbButtons?.prev) {
            this.thumbButtons.prev.addEventListener('click', (e) => {
              e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
              this.goThumbWrapped(-1);
            });
          }
          if (this.thumbButtons?.next) {
            this.thumbButtons.next.addEventListener('click', (e) => {
              e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
              this.goThumbWrapped(1);
            });
          }
        }

        /* ========= WHEEL + KEYBOARD NAV ========= */
        this.installWheelNavigation();
        this.installKeyboardNavigation();

        // Optional: remove list semantics on desktop thumbnail layout
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      /* ================== UTILITIES ================== */

      debounce(fn, wait = 200) {
        let t;
        return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
      }

      throttle(fn, wait = 200) {
        let last = 0;
        return (...args) => {
          const now = Date.now();
          if (now - last >= wait) { last = now; fn.apply(this, args); }
        };
      }

      viewerList() {
        return this.querySelector('[id^="Slider-Gallery"]'); // main UL
      }

      thumbsList() {
        return this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]') || null; // thumbnail UL
      }

      visibleMediaItems() {
        const ul = this.viewerList();
        if (!ul) return [];
        return Array.from(ul.querySelectorAll('.product__media-item')).filter(el => !el.classList.contains('hide-media'));
      }

      currentActiveIndex() {
        const ul = this.viewerList();
        const items = this.visibleMediaItems();
        if (!ul || !items.length) return 0;
        const active = ul.querySelector('.product__media-item.is-active') || items[0];
        const idx = items.indexOf(active);
        return idx >= 0 ? idx : 0;
      }

      /* ================== MAIN ARROWS (WRAP) ================== */

      goPrevWrapped() {
        const items = this.visibleMediaItems();
        if (!items.length) return;
        const i = this.currentActiveIndex();
        const targetIndex = (i - 1 + items.length) % items.length;
        const target = items[targetIndex];
        this.activateMediaByItem(target);
      }

      goNextWrapped() {
        const items = this.visibleMediaItems();
        if (!items.length) return;
        const i = this.currentActiveIndex();
        const targetIndex = (i + 1) % items.length;
        const target = items[targetIndex];
        this.activateMediaByItem(target);
      }

      activateMediaByItem(item) {
        if (!item) return;
        const id = item.dataset?.mediaId;
        if (id) {
          this.setActiveMedia(id, false); // Dawn-compatible scroll + sync
        } else {
          // Fallback: direct scroll (shouldn’t be needed)
          const ul = this.viewerList();
          item.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
          ul?.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
          this.visibleMediaItems().forEach(el => el.classList.remove('is-active'));
          item.classList.add('is-active');
        }
        this.defer(() => this.enableMainArrows(), 0); // never locked
      }

      enableMainArrows() {
        if (this.prevButton) this.prevButton.disabled = false;
        if (this.nextButton) this.nextButton.disabled = false;
      }

      installMainArrowsObserver() {
        const reEnable = (mutations) => {
          for (const m of mutations) {
            if (m.type === 'attributes' && m.attributeName === 'disabled') {
              this.enableMainArrows();
            }
          }
        };
        if (this.prevButton) new MutationObserver(reEnable).observe(this.prevButton, { attributes: true, attributeFilter: ['disabled'] });
        if (this.nextButton) new MutationObserver(reEnable).observe(this.nextButton, { attributes: true, attributeFilter: ['disabled'] });
      }

      defer(fn, ms = 0) { return setTimeout(fn, ms); }

      /* ================== ZOOM CLICK ================== */

      handleMainViewerClick(event) {
        event.preventDefault(); event.stopPropagation();
        if (event.target.closest('.slider-button')) return; // ignore controls
        const ul = this.viewerList();
        const activeItem = ul?.querySelector('.product__media-item.is-active');
        if (!activeItem) return;
        this.zoomState = (this.zoomState + 1) % 3;
        activeItem.classList.remove('is-zoomed-1', 'is-zoomed-2');
        if (this.zoomState === 1) activeItem.classList.add('is-zoomed-1');
        else if (this.zoomState === 2) activeItem.classList.add('is-zoomed-2');
      }

      /* ================== SLIDE CHANGED ================== */

      onSlideChanged(event) {
        // Reset zoom
        this.zoomState = 0;
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.remove('is-zoomed-1', 'is-zoomed-2');
        });

        // Keep arrows enabled (wrap mode)
        this.enableMainArrows();

        // Sync thumbnail selection
        if (!this.elements.thumbnails) return;
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      /* ================== ACTIVATE MEDIA (Dawn-compatible) ================== */

      setActiveMedia(mediaId, prepend) {
        const ul = this.viewerList();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Clear active + zoom
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
          el.classList.remove('is-active', 'is-zoomed-1', 'is-zoomed-2');
        });
        this.zoomState = 0;
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumb && activeThumb.parentElement.firstChild !== activeThumb) {
              activeThumb.parentElement.prepend(activeThumb);
            }
          }
          this.elements.viewer.resetPages?.();
        }

        // Prevent sticky header reveal
        this.preventStickyHeader();

        // Scroll horizontally to active media; then fix vertical if needed
        this.defer(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const rect = activeMedia.getBoundingClientRect();
          if (rect.top <= -0.5) {
            const top = rect.top + window.scrollY;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }, 0);

        // Playback control
        this.playActiveMedia(activeMedia);

        // Sync thumbnails + announce
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        this.enableMainArrows(); // never locked
      }

      /* ================== THUMBNAIL SYNC & SCROLL ================== */

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Update aria-current
        this.elements.thumbnails.querySelectorAll('button').forEach((el) => el.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        // If already visible, skip
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        const scroller =
          this.thumbsList() ||
          this.elements.thumbnails.slider ||
          this.elements.thumbnails;

        scroller.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      /* ================== A11Y ANNOUNCE ================== */

      announceLiveRegion(activeItem, position) {
        const image =
          activeItem.querySelector('.product__modal-opener--image img') ||
          activeItem.querySelector('img');
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          const msg = window.accessibilityStrings?.imageAvailable?.replace?.('[index]', position) || '';
          this.elements.liveRegion.innerHTML = msg;
          setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
        };
        image.src = image.src; // trigger cached onload
      }

      /* ================== MEDIA PLAYBACK ================== */

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        activeItem.querySelector('.deferred-media')?.loadContent(false);
      }

      /* ================== STICKY HEADER GUARD ================== */

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
      }

      /* ================== DESKTOP SEMANTICS ================== */

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach?.((slide) => slide.setAttribute('role', 'presentation'));
      }

      /* ================== THUMB STRIP: INFINITE WRAP BY STEP ================== */

      thumbItems() {
        const list = this.thumbsList();
        return list ? Array.from(list.querySelectorAll('.slider__slide')) : [];
      }

      thumbScroller() {
        // Prefer the UL (true scroller); fall back to component's .slider or the component itself
        return this.thumbsList() || this.elements.thumbnails?.slider || this.elements.thumbnails;
      }

      thumbStartIndex() {
        const items = this.thumbItems();
        const scroller = this.thumbScroller();
        if (!items.length || !scroller) return 0;
        const x = scroller.scrollLeft;
        let min = Infinity, idx = 0;
        items.forEach((li, i) => {
          const d = Math.abs(x - li.offsetLeft);
          if (d < min) { min = d; idx = i; }
        });
        return idx;
      }

      thumbStep(dir) {
        const btn = dir > 0 ? this.thumbButtons?.next : this.thumbButtons?.prev;
        const step = Number(btn?.dataset?.step || 3);
        return Number.isFinite(step) && step > 0 ? step : 3;
      }

      goThumbWrapped(dir) {
        const scroller = this.thumbScroller();
        const items = this.thumbItems();
        if (!scroller || !items.length) return;

        const start = this.thumbStartIndex();
        const stepSigned = this.thumbStep(dir) * (dir > 0 ? 1 : -1);
        let target = start + stepSigned;

        const len = items.length;
        target = ((target % len) + len) % len;

        scroller.scrollTo({ left: items[target].offsetLeft, behavior: 'smooth' });
      }

      /* ================== WHEEL NAVIGATION ================== */

      installWheelNavigation() {
        // MAIN GALLERY: wheel to previous/next
        const ul = this.viewerList();
        if (ul) {
          const onWheelMain = this.throttle((e) => {
            // Only act when user is over the gallery; prevent page scroll here
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Prefer horizontal delta (trackpads), else use vertical
            const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : 0;
            const dy = dx ? 0 : e.deltaY;

            const dir = (dx || dy) > 0 ? 1 : -1; // positive → next, negative → prev
            if (dir > 0) this.goNextWrapped();
            else this.goPrevWrapped();
          }, 180);

          ul.addEventListener('wheel', onWheelMain, { passive: false });
        }

        // THUMBNAIL STRIP: wheel to scroll by step
        const tul = this.thumbsList();
        if (tul) {
          const onWheelThumbs = this.throttle((e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : 0;
            const dy = dx ? 0 : e.deltaY;

            const dir = (dx || dy) > 0 ? 1 : -1;
            this.goThumbWrapped(dir);
          }, 160);

          tul.addEventListener('wheel', onWheelThumbs, { passive: false });
        }
      }

      /* ================== KEYBOARD NAVIGATION ================== */

      installKeyboardNavigation() {
        // Left/Right arrows move main gallery
        this.addEventListener('keydown', (e) => {
          // Only respond when focus is within the media-gallery region
          if (e.key === 'ArrowRight') {
            e.preventDefault(); e.stopPropagation();
            this.goNextWrapped();
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault(); e.stopPropagation();
            this.goPrevWrapped();
          }
        });

        // Make the component focusable for keyboard nav if it isn't already
        if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      }
    }
  );
}
